import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Bookmark, X, MapPin, PlusCircle } from 'lucide-react';
import { LibraryItem } from './types';
import { LibraryService } from '@/lib/library-service';
import { getItemTitle, getItemLocation, extractLocationData, itemToLibraryItem } from '@/lib/library-utils';
import { ItemTypeSelector } from './ItemTypeSelector';
import { CreateItemForm } from './CreateItemForm';
import { toast } from '@/components/ui/use-toast';

// Adicionamos um modo ao modal
type ModalMode = 'save' | 'select_type' | 'create_item';

interface SaveToLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  item?: any;
  itemType?: string;
  onSave: (libraryItem: LibraryItem) => void;
}

export function SaveToLibraryModal({
  isOpen,
  onClose,
  item,
  itemType,
  onSave
}: SaveToLibraryModalProps) {
  // Estados para os campos
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  
  // Estado para controlar o modo do modal
  const [mode, setMode] = useState<ModalMode>('save');
  const [selectedItemType, setSelectedItemType] = useState<string>('');
  
  // Add a loading state at the top of the function component
  const [isSaving, setIsSaving] = useState(false);
  
  // Extrair título e localização do item quando estiver no modo 'save'
  useEffect(() => {
    if (item && itemType && mode === 'save') {
      const initialTitle = getItemTitle(item, itemType);
      const initialLocation = getItemLocation(item, itemType);
      const locationData = extractLocationData(item, itemType);
      
      setTitle(initialTitle);
      setDescription('');
      setTags([]);
      setCountry(locationData.country || '');
      setCity(locationData.city || '');
    }
  }, [item, itemType, mode]);
  
  // Resetar estado quando o modal é fechado
  useEffect(() => {
    if (!isOpen) {
      setMode('save');
      setSelectedItemType('');
    }
  }, [isOpen]);
  
  // Adicionar uma tag
  const addTag = () => {
    if (tagInput && !tags.includes(tagInput)) {
      setTags([...tags, tagInput]);
      setTagInput('');
    }
  };
  
  // Remover uma tag
  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };
  
  // Handler para tecla Enter no campo de tag
  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      addTag();
    }
  };
  
  // Salvar o item na biblioteca
  const handleSave = async () => {
    if (!title) {
      // Exibir erro caso o título esteja vazio
      toast({
        title: "Erro ao salvar",
        description: "O título é obrigatório.",
        variant: "destructive"
      });
      return;
    }
    
    // Set loading state
    setIsSaving(true);
    
    // Criar item para a biblioteca - don't generate ID, let Supabase handle it
    const libraryItem = itemToLibraryItem(item, itemType, {
      title,
      description,
      tags,
      city,
      country,
      generateId: false // Don't generate an ID, let Supabase create one
    });
    
    try {
      // Salvar o item na biblioteca
      const savedItem = await LibraryService.saveItem(libraryItem);
      
      // Chamar o callback com o item salvo
      onSave(savedItem);

      // Exibir toast de sucesso
      toast({
        title: "Item salvo",
        description: "Item salvo na biblioteca com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao salvar item na biblioteca:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar o item na biblioteca.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  // Tratar a criação de um novo item
  const handleCreateNewItem = () => {
    setMode('select_type');
  };
  
  // Quando um tipo é selecionado no seletor de tipos
  const handleSelectItemType = (type: string) => {
    setSelectedItemType(type);
    setMode('create_item');
  };
  
  // Quando um item é criado no formulário de criação
  const handleItemCreated = (libraryItem: LibraryItem) => {
    // Notificar o componente pai (mesmo tratamento de quando salva um item existente)
    onSave(libraryItem);
  };
  
  // Função para voltar ao modo anterior
  const handleGoBack = () => {
    if (mode === 'create_item') {
      setMode('select_type');
    } else if (mode === 'select_type') {
      setMode('save');
    }
  };
  
  // Decidir qual conteúdo mostrar baseado no modo
  const renderContent = () => {
    switch (mode) {
      case 'select_type':
        return (
          <ItemTypeSelector 
            isOpen={true} 
            onClose={() => setMode('save')}
            onSelectType={handleSelectItemType}
          />
        );
      
      case 'create_item':
        return (
          <CreateItemForm 
            isOpen={true}
            itemType={selectedItemType as any}
            onClose={() => setMode('select_type')}
            onItemCreated={handleItemCreated}
          />
        );
      
      default: // modo 'save'
        return (
          <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[550px]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Bookmark className="h-5 w-5 text-amber-500" />
                  Salvar na Biblioteca
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 pt-3">
                {/* Botão para criar novo item - SEMPRE VISÍVEL */}
                <Button 
                  variant="outline" 
                  className="w-full border-dashed border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={handleCreateNewItem}
                >
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Criar novo item
                </Button>
                
                {/* Formulário de salvamento de item existente */}
                {item && itemType ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="lib-title">Título *</Label>
                      <Input
                        id="lib-title"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        placeholder="Dê um título para este item na biblioteca"
                        required
                      />
                    </div>
                    
                    {/* Descrição */}
                    <div className="space-y-2">
                      <Label htmlFor="lib-desc">Descrição</Label>
                      <Textarea
                        id="lib-desc"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder="Descrição opcional para facilitar a busca depois"
                        rows={3}
                      />
                    </div>
                    
                    {/* Localização */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="lib-country">País</Label>
                        <Input
                          id="lib-country"
                          value={country}
                          onChange={e => setCountry(e.target.value)}
                          placeholder="País"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="lib-city">Cidade</Label>
                        <Input
                          id="lib-city"
                          value={city}
                          onChange={e => setCity(e.target.value)}
                          placeholder="Cidade"
                        />
                      </div>
                    </div>
                    
                    {/* Tags */}
                    <div className="space-y-2">
                      <Label htmlFor="lib-tags">Tags</Label>
                      <div className="flex gap-2">
                        <Input
                          id="lib-tags"
                          value={tagInput}
                          onChange={e => setTagInput(e.target.value)}
                          placeholder="Adicione tags para organização"
                          onKeyDown={handleTagKeyDown}
                        />
                        <Button type="button" onClick={addTag} variant="outline">Adicionar</Button>
                      </div>
                      
                      {/* Tags exibidas */}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {tags.map(tag => (
                          <Badge key={tag} className="flex items-center gap-1 pl-2 pr-1 py-1">
                            {tag}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-4 w-4 ml-1"
                              onClick={() => removeTag(tag)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </Badge>
                        ))}
                        {tags.length === 0 && (
                          <span className="text-gray-400 text-sm">Nenhuma tag adicionada</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Preview do item */}
                    <div className="mt-4 p-3 border rounded-md bg-gray-50">
                      <h4 className="text-sm font-medium mb-2">Preview</h4>
                      <div className="flex items-start gap-3">
                        {item.images && item.images.length > 0 && (
                          <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0">
                            <img 
                              src={item.images[0]} 
                              alt="Thumbnail" 
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{title || getItemTitle(item, itemType)}</p>
                          {getItemLocation(item, itemType) && (
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <MapPin className="h-3.5 w-3.5" />
                              {getItemLocation(item, itemType)}
                            </p>
                          )}
                          {description && (
                            <p className="text-sm mt-1 line-clamp-2">{description}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="py-8 text-center text-muted-foreground">
                    <p>Não há item para salvar.</p>
                    <p className="mt-2">Clique em "Criar novo item" para adicionar um item diretamente à biblioteca.</p>
                  </div>
                )}
              </div>
              
              <DialogFooter className="flex justify-between">
                <Button variant="outline" onClick={onClose}>Cancelar</Button>
                {item && itemType && (
                  <Button 
                    onClick={handleSave}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                    disabled={!title.trim() || isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span className="animate-spin mr-2">⟳</span>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Bookmark className="mr-2 h-4 w-4" />
                        Salvar na Biblioteca
                      </>
                    )}
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
    }
  };
  
  return (
    <>
      {renderContent()}
    </>
  );
} 